import React from 'react';
import moment from 'moment';
import { StyledSelector, StyledOption, StyledRepeatSelector, StyledDaySelect, StyledWrxSelector, WrxSelect } from '../../styles/devices/Selectors';

/**
 * This is a generic expression to use across drop downs in Edit panel on the devices page
 * @param    {any}     parameter    Current selected value from the drop down
 * @param    {any}     onChange
 * @param    {Array}   Array        Elements that needs to be displayed on the drop down 
 * @param    {string}  type         Type of the list (ex: hour, duration), this identifies what to display on the drop down
 * @returns 
 */
export const StandardSelector = ({parameter, onChange, array, type}: {parameter: any; onChange?: any; array: Array<any>; type?: string|null }) => {
    
    var param:any; 
    var val:any;
    
    // Identifies the type of the dropdwon
    switch(type){

        // If the type is duration, convert the paramter to hours
        // As we need to display in hours (1hr, 2hr, 3hr etc)
        case 'Duration':
            param = parameter/3600;
            break;
        
        // If the type is hour, convert the paramter to 'H' format
        case 'Hour':
            param = moment(parameter).format('H');
            break;
        
        // If no type is present just display the paremeter as it is    
        case null:
            param = parameter;
    }

    // Returns a formatted array depending on the `type` 
    return(
        <StyledSelector
            value={param}
            onChange={onChange}
        >
            {array.map((value, index) => {
                
                switch(type){
                    case 'Duration':                        
                        val = value.toString() + 'hr'; 
                        break;                          
                    
                    case 'Hour':                       
                        val = value.toString().padStart(2, '0') + ':00';     
                        break;                
            
                    case 'Band':
                        if (value==='Broadband') val = value;                
                        else val = value + 'Hz';     
                        break; 

                    case undefined:
                        val = value;
                        
                }             
                return <StyledOption key={index} value={value}>{val}</StyledOption>
            })}    
        </StyledSelector>
    )
}

/**
 * This is a generic expression to use across week days in Edit panel on the devices page
 * 
 * @param    {Array | number}   selected      Current selected value/values from seven days
 * @param    {Array}            labels        Array containing labels for each day
 * @param    {any}              onChange
 * @param    {string}           type          This differentiate whether this is a multiple day select or a single day select
 * @returns                                   Selected days/day
 */
export const DaySelector = ({selected, labels, onChange, type}) => {
    
    // This expression allows to identify multiple/single selected days upon on click
    const onClickRepeat = (value) => {
        return () => {
            // Assign a new array 
            let new_selected = selected.map(item => item);
            
            // Checks if the current selected day already exist in the `selected` list
            // If not push that value to the `new_selected` array
            if (!selected.includes(value)) {            
                new_selected.push(value);            
            } 
            // Else identify the value (day)
            else {
            new_selected = selected.filter((item) => {
                if (item !== value) {
                return true;
                }
            })
            }
            // Assign `new_selected` to be `selected` array
            onChange({ selected: new_selected })
        }
    }

    // This expression allows to identify selected day upon on click
    const onClickSingle = (value) => {    
        // Assign empty array
        let new_selected = [];      
        return () => {
            // previously selected value is not equal to current onclick change
            // Push that value to `new_selected`
            if (selected !== value){             
              new_selected.push(value);
            }
            // Assign `new_selected` to 'selected'
            onChange({ selected: new_selected })
        }
    }
    
    // Returns days array with selected days or day depending on the page type.
    // Ex: `Weeky Restart` cannot have multiple selection of days, 
    // Therefore, its type would be `DaySingle`, it will only return one selected day.
    return (
        <StyledRepeatSelector>
            {labels.map((item, index) => {

                switch(type){
                    case 'DayRepeat':
                        return <StyledDaySelect key={index} onClick={onClickRepeat(index)} selected={selected.includes(index)}>{item}</StyledDaySelect>
                        
                    case 'DaySingle':
                        return <StyledDaySelect key={index} onClick={onClickSingle(index)} selected={selected[0] === index}>{item}</StyledDaySelect>

                }               
            
            })}
        </StyledRepeatSelector>
    )
}

/**
 * This is use as an option selector
 * 
 * @param      {number}            Selected         Current selected value
 * @param      {wrx_options}       wrx_options      Array containing labels for each wrx_option
 * @param      {onChange}          onChange
 * @returns 
 */
export const WrxOptionSelector = ({selected, wrx_options, onChange}) => {
    
    // If nothing is selected at the begining
    // Get `selected` as 1
    if (!selected){    
        selected = 1;
    } 
        
    const onClick = (value) => {
        // Assign empty array
        let new_selected = selected;
        return () => {      

        // previously selected value is not equal to current onclick change
        // Push that value to `new_selected`
        if (selected !== value){
            new_selected = value;        
        }

        // Assign `new_selected` to 'selected'
        onChange({ selected: new_selected})
        }
    } 
    
    // Returns wrx_options array with the selected option
    return (
        <StyledWrxSelector>      
            {wrx_options.map((wrx_option, index) => {
                return <WrxSelect onClick={onClick(wrx_option.wrx_option_id)} selected={selected===wrx_option.wrx_option_id} key={index}>{wrx_option.description}</WrxSelect>
            })}
        </StyledWrxSelector>
    )
  
}

